import { useEffect, useEffectEvent, useMemo, useRef } from 'react';

import { useForwardCatalog, useSpotCatalog } from '@/features/catalog';
import { AppFormProvider } from '@/features/dashboard/components/AppForm/AppForm/context';
import { DashboardDrawer } from '@/features/dashboard/components/Drawer';
import {
  DrawerStepsProvider,
  useDrawerSteps
} from '@/features/dashboard/components/Drawer/context/DrawerStepsContext';
import DrawerOpenRequestHandler from '@/features/dashboard/components/DrawerOpenRequestHandler';
import { DashboardStep } from '@/features/dashboard/constants/DashboardStep';
import { useDashboardRouting } from '@/features/dashboard/hooks/routing/useDashboardRouting';
import { useDashboardStepControl } from '@/features/dashboard/hooks/routing/useDashboardStepControl';
import { EmptyDashboard } from '@/features/dashboard/pages/Empty';
import { ViewDashboard } from '@/features/dashboard/pages/View';
import { useDashboardEditLockContext } from '@/features/dashboard/state/DashboardEditLockContext';
import { MarketsSkeleton } from '@/features/markets';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';

import { EditDashboard } from './pages/Edit/EditDashboardPage';

const EditLockDrawerSync = ({
  setStep,
  step
}: {
  setStep: (step: DashboardStep) => void;
  step: DashboardStep;
}) => {
  const { setForceReleaseCallback, releaseEditLock } = useDashboardEditLockContext();
  const { closeDrawer, drawerStep } = useDrawerSteps();
  const prevDrawerStepRef = useRef(drawerStep);

  const forceReleaseEdit = useEffectEvent(() => {
    setStep(DashboardStep.View);
    closeDrawer();
  });

  useEffect(() => {
    setForceReleaseCallback(forceReleaseEdit);
    return () => {
      setForceReleaseCallback(() => {});
    };
  }, [setForceReleaseCallback]);

  useEffect(() => {
    const prev = prevDrawerStepRef.current;
    prevDrawerStepRef.current = drawerStep;
    if (prev !== null && drawerStep === null && step !== DashboardStep.Edit) {
      releaseEditLock();
    }
  }, [drawerStep, step, releaseEditLock]);

  return null;
};

const Dashboard = () => {
  const {
    step,
    setStep,
    guardedSetStep,
    dashboardConfig,
    dashboardStructure,
    activeTab,
    tabNames,
    handleTabChange,
    routeDashboardName,
    isLoading
  } = useDashboardRouting();

  useDashboardStepControl(step);

  useForwardCatalog();
  useSpotCatalog();

  const displayStep = useMemo(() => {
    if (step === DashboardStep.Edit) return DashboardStep.Edit;
    if (!isLoading && (!dashboardConfig || dashboardConfig.length === 0))
      return DashboardStep.Empty;
    return DashboardStep.View;
  }, [dashboardConfig, isLoading, step]);

  const content = useMemo(() => {
    if (isLoading) {
      return <MarketsSkeleton />;
    }
    switch (displayStep) {
      case DashboardStep.Empty:
        return (
          <EmptyDashboard
            activeTab={activeTab}
            setStep={guardedSetStep}
            tabNames={tabNames}
            title={routeDashboardName}
            onTabChange={handleTabChange}
          />
        );
      case DashboardStep.View:
        return (
          <ViewDashboard
            activeTab={activeTab}
            dashboardConfig={dashboardConfig}
            setStep={guardedSetStep}
            tabNames={tabNames}
            title={routeDashboardName}
            onTabChange={handleTabChange}
          />
        );
      case DashboardStep.Edit:
        return (
          <EditDashboard
            activeTab={activeTab}
            dashboardConfig={dashboardConfig ?? []}
            dashboardName={routeDashboardName}
            dashboardStructure={dashboardStructure}
            initialDashboardName={routeDashboardName}
            onClose={() => {
              setStep(DashboardStep.View);
            }}
          />
        );
      default:
        return null;
    }
  }, [
    isLoading,
    displayStep,
    activeTab,
    tabNames,
    handleTabChange,
    dashboardConfig,
    routeDashboardName,
    dashboardStructure,
    guardedSetStep,
    setStep
  ]);

  return (
    <PageInformationContext
      value={{
        mainPageTitle: routeDashboardName
      }}
    >
      <AppFormProvider>
        <DrawerStepsProvider>
          <DrawerOpenRequestHandler />
          <EditLockDrawerSync
            setStep={setStep}
            step={step}
          />
          {content}
          <DashboardDrawer />
        </DrawerStepsProvider>
      </AppFormProvider>
    </PageInformationContext>
  );
};

export default Dashboard;
